import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { ButtonDesigns } from 'src/components/Button/Button'
import ModalDialogForm from 'src/components/ModalDialogForm/ModalDialogForm'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { usePostCreateDepartment } from 'src/queries/organizations/department.queries'
import { PostDepartmentRequest } from 'src/queries/organizations/departments.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import {
  stringLengthIsBetween,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import styled, { css } from 'styled-components'

interface CreateDepartmentFormProps {
  closeCreateDepartmentForm: () => void
}

const InputSpan = styled.span`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`
const CreateDepartmentForm: React.FC<CreateDepartmentFormProps> = (props) => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const { analytics } = useAnalyticsContext()

  const {
    fields: newDepartmentFlatFields,
    setFields: setNewDepartmentFlatFields,
    fieldsErrors: newDepartmentFlatFieldsErrors,
    setErrorsAndReturnDidPassValidation: departmentFlatFieldsSetErrors,
  } = useFormFields<PostDepartmentRequest>(
    {
      departmentName: '',
      departmentDescription: '',
      departmentCode: '',
    },
    (): FormFieldsValidation<PostDepartmentRequest> => ({
      departmentName: [stringLengthIsBetween(1, 50)],
      departmentDescription: [stringLengthIsLessThan(200)],
      departmentCode: [stringLengthIsLessThan(20)],
    }),
  )

  const isConfirmationFormValid =
    newDepartmentFlatFields.departmentName.length > 0 &&
    newDepartmentFlatFields.departmentName.length <= 50
  const {
    mutate: postCreateDepartment,
    isPending: postCreateDepartmentLoading,
    error: postCreateDepartmentError,
  } = usePostCreateDepartment(organizationId)
  const takeStringIfNotEmpty = (val?: string): string | undefined =>
    (val?.trim()?.length || 0) > 0 ? val : undefined

  return (
    <ModalDialogForm
      formOnSubmit={(e: React.FormEvent): void => {
        e.preventDefault()
        analytics.track('departments - add department')
        if (departmentFlatFieldsSetErrors()) {
          postCreateDepartment({
            organizationId: organizationId,
            body: {
              name: newDepartmentFlatFields.departmentName,
              description: takeStringIfNotEmpty(
                newDepartmentFlatFields.departmentDescription,
              ),
              code: takeStringIfNotEmpty(
                newDepartmentFlatFields.departmentCode,
              ),
            },
          })
        }
      }}
      actionButtonLabel={translate('Add Team')}
      actionButtonLoading={postCreateDepartmentLoading}
      actionButtonDesign={ButtonDesigns.designCta}
      cancelButtonLabel={translate('Cancel')}
      cancelButtonOnClick={props.closeCreateDepartmentForm}
      showCancelAsLink
      isFormValid={isConfirmationFormValid}
      error={postCreateDepartmentError?.error}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 40px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate('Add Team')}
        </span>
        <InputSpan>
          <TextInputField
            design={TextInputFieldDesigns.New}
            placeholder={translateSingleString(locale, 'Enter a team name')}
            fieldLabel={
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate('Team Name')}
              </span>
            }
            value={newDepartmentFlatFields.departmentName}
            characterLimit={50}
            onChange={(value: string): void =>
              setNewDepartmentFlatFields({
                ...newDepartmentFlatFields,
                departmentName: value,
              })
            }
            errors={newDepartmentFlatFieldsErrors.departmentCode}
            testId={'departmentName'}
          />
        </InputSpan>
        <InputSpan>
          <TextInputField
            design={TextInputFieldDesigns.New}
            placeholder={translateSingleString(
              locale,
              'Enter a team description',
            )}
            fieldLabel={
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate('Team Description')}
              </span>
            }
            value={newDepartmentFlatFields.departmentDescription}
            characterLimit={200}
            hideCharacterLimitCount
            isOptional
            onChange={(value: string): void =>
              setNewDepartmentFlatFields({
                ...newDepartmentFlatFields,
                departmentDescription: value,
              })
            }
            errors={newDepartmentFlatFieldsErrors.departmentDescription}
            testId={'departmentDescription'}
          />
        </InputSpan>
        <InputSpan>
          <TextInputField
            design={TextInputFieldDesigns.New}
            placeholder={translateSingleString(locale, 'Enter a team code')}
            fieldLabel={
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate('Team Code')}
              </span>
            }
            value={newDepartmentFlatFields.departmentCode}
            characterLimit={20}
            hideCharacterLimitCount
            isOptional
            onChange={(value: string): void =>
              setNewDepartmentFlatFields({
                ...newDepartmentFlatFields,
                departmentCode: value,
              })
            }
            errors={newDepartmentFlatFieldsErrors.departmentCode}
            testId={'departmentCode'}
          />
        </InputSpan>
      </div>
    </ModalDialogForm>
  )
}

export default CreateDepartmentForm
